import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ShareButton.css';

const ShareIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 16V3m-5 5 5-5 5 5M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6"/></svg>;

export default function ShareAction({url:shareUrl, getUrl, previewUrl = '', title, brand = 'Sports HQ', help, label = 'Dela', disabled = false}) {
  const [resolvedUrl, setResolvedUrl] = useState(shareUrl || "");
  const url = getUrl ? resolvedUrl : shareUrl;
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const trigger = useRef(null), dialog = useRef(null), input = useRef(null);
  const close = () => { setOpen(false); setNotice(''); };
  useEffect(() => { close(); }, [shareUrl, title]);
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const first = dialog.current.querySelector('button');
    first?.focus();
    const keydown = event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); }
      if (event.key !== 'Tab') return;
      const elements = [...dialog.current.querySelectorAll('button:not(:disabled), a[href], input')];
      const firstElement = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); firstElement.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', keydown); trigger.current?.focus(); };
  }, [open]);
  const share = async () => {
    setNotice('');
    let sharingUrl = url;
    if (getUrl) {
      setBusy(true);
      try { sharingUrl = await getUrl(); setResolvedUrl(sharingUrl); }
      catch (error) { setNotice(error.message || 'Länken kunde inte skapas. Försök igen.'); setOpen(true); return; }
      finally { setBusy(false); }
    }
    if (navigator.share) {
      setBusy(true);
      try { await navigator.share({ title: `${title} | ${brand}`, text: title, url: sharingUrl }); return; }
      catch (error) { if (error.name === 'AbortError') return; }
      finally { setBusy(false); }
    }
    setOpen(true);
  };
  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw Error('clipboard');
      await navigator.clipboard.writeText(url);
      setNotice('Länken är kopierad. Klistra in den i din chatt.');
    } catch {
      input.current.focus(); input.current.select();
      setNotice('Länken är markerad. Välj Kopiera och klistra sedan in den i din chatt.');
    }
  };
  return <>
    <button ref={trigger} className="hq-share-button" type="button" onClick={share} disabled={disabled || (!url && !getUrl) || busy} aria-label={`${label}: ${title}`}><ShareIcon/><span>{label}</span></button>
    {open && createPortal(<div className="hq-share-backdrop" onClick={event => { if (event.target === event.currentTarget) close(); }}>
      <section ref={dialog} className="hq-share-dialog" role="dialog" aria-modal="true" aria-labelledby="hq-share-title" aria-describedby="hq-share-help">
        <div className="hq-share-dialog__heading"><div><span className="hq-share-eyebrow">Skicka vidare</span><h2 id="hq-share-title">Dela med någon</h2></div><button className="hq-share-close" type="button" aria-label="Stäng delning" onClick={close}>×</button></div>
        <div className="hq-share-preview">{previewUrl && <img src={previewUrl} alt="" width="1200" height="630" onError={event => { event.currentTarget.hidden = true; }}/>}<div><small>{brand}</small><strong>{title}</strong><span>{new URL(url || window.location.origin).hostname}</span></div></div>
        <p id="hq-share-help">Skicka länken i din chatt. {help || "Den öppnar samma match, lag eller serie för den som får den."}</p>
        <div className="hq-share-actions"><button className="hq-share-copy" type="button" disabled={!url} onClick={copy}>{notice.startsWith('Länken är kopierad') ? '✓ Länk kopierad' : 'Kopiera länk'}</button><a href={url ? `https://wa.me/?text=${encodeURIComponent(`${title}\n${url}`)}` : undefined} aria-disabled={!url} tabIndex={url ? undefined : -1} target="_blank" rel="noopener noreferrer">WhatsApp <span aria-hidden="true">↗</span></a></div>
        <label className="hq-share-link">Länk att dela<input ref={input} value={url} readOnly onFocus={event => event.target.select()} /></label>
        <p className="hq-share-notice" role="status" aria-live="polite">{notice || 'På mobilen kan du också välja Signal, Snapchat och andra appar i telefonens delningsmeny.'}</p>
      </section>
    </div>, document.body)}
  </>;
}
